import { useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, FileText, FileSpreadsheet, FileDown, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * ImportExportBar — reusable for every master.
 * Props:
 *   masterKey (str): backend key e.g. "companies", "categories", "groups", "hsn-codes", "items", "rates"
 *   filters: { search, from_date, to_date } — appended to export URLs
 *   onImported (fn): callback after successful import (to refresh list)
 */
export default function ImportExportBar({ masterKey, filters = {}, onImported }) {
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const token = () => localStorage.getItem("erp_token");

  const buildExportUrl = (fmt) => {
    const p = new URLSearchParams();
    if (filters.search) p.set("search", filters.search);
    if (filters.from_date) p.set("from_date", filters.from_date);
    if (filters.to_date) p.set("to_date", filters.to_date);
    const qs = p.toString();
    return `${API}/masters/${masterKey}/export.${fmt}${qs ? "?" + qs : ""}`;
  };

  const download = async (url, filename) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` }, credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const getTemplate = () => download(`${API}/masters/${masterKey}/template.csv`, `${masterKey}_template.csv`);
  const exportCsv = () => download(buildExportUrl("csv"), `${masterKey}_export.csv`);
  const exportXlsx = () => download(buildExportUrl("xlsx"), `${masterKey}_export.xlsx`);
  const exportPdf = () => download(buildExportUrl("pdf"), `${masterKey}_export.pdf`);

  const doPreview = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/masters/${masterKey}/import?dry_run=true`,
        { method: "POST", headers: { Authorization: `Bearer ${token()}` }, credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setImporting(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/masters/${masterKey}/import`,
        { method: "POST", headers: { Authorization: `Bearer ${token()}` }, credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`${data.inserted || 0} inserted · ${data.updated || 0} updated${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      setImportOpen(false); setFile(null); setPreview(null);
      onImported?.();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setImporting(false); }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Button data-testid={`import-${masterKey}-btn`} size="sm" variant="outline" onClick={() => setImportOpen(true)} className="h-9 rounded-sm text-xs uppercase tracking-wider"><Upload className="w-3 h-3 mr-1"/> Import</Button>
        <div className="flex border border-border rounded-sm overflow-hidden">
          <button data-testid={`export-csv-${masterKey}`} onClick={exportCsv} className="px-3 h-9 text-xs uppercase tracking-wider flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-secondary" title="Export CSV"><FileText className="w-3 h-3"/> CSV</button>
          <button data-testid={`export-xlsx-${masterKey}`} onClick={exportXlsx} className="px-3 h-9 text-xs uppercase tracking-wider flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-secondary border-l border-border" title="Export Excel"><FileSpreadsheet className="w-3 h-3"/> Excel</button>
          <button data-testid={`export-pdf-${masterKey}`} onClick={exportPdf} className="px-3 h-9 text-xs uppercase tracking-wider flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-secondary border-l border-border" title="Export PDF"><FileDown className="w-3 h-3"/> PDF</button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setFile(null); setPreview(null); } }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">Import to {masterKey}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 text-xs flex items-center justify-between">
              <span>Not sure about the columns? <span className="text-primary font-semibold">Download the sample template</span> first.</span>
              <Button data-testid="download-template" size="sm" variant="ghost" onClick={getTemplate} className="text-xs text-primary"><Download className="w-3 h-3 mr-1"/> Template CSV</Button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">CSV or Excel file (.csv, .xlsx)</Label>
              <Input data-testid="import-file" type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div className="flex gap-2">
              <Button data-testid="preview-import" onClick={doPreview} disabled={!file || importing} variant="outline" className="rounded-sm text-xs uppercase tracking-wider">Preview</Button>
              <Button data-testid="confirm-import" onClick={doImport} disabled={!file || importing || (preview && preview.count === 0)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">{importing ? "Working…" : "Import"}</Button>
            </div>
            {preview && (
              <div className="border border-border rounded-sm">
                <div className="p-3 border-b border-border grid grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-green-500"><CheckCircle2 className="w-3.5 h-3.5"/> <span className="font-mono font-semibold" data-testid="preview-count">{preview.count}</span> valid</div>
                  <div className="flex items-center gap-2 text-destructive"><AlertCircle className="w-3.5 h-3.5"/> <span className="font-mono font-semibold">{preview.errors.length}</span> errors</div>
                  <div className="text-muted-foreground text-right">Total rows: {preview.total_rows}</div>
                </div>
                {preview.preview.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-secondary text-[9px] uppercase tracking-widest text-muted-foreground">
                        <tr>{Object.keys(preview.preview[0]).map(k => <th key={k} className="text-left p-2">{k}</th>)}</tr>
                      </thead>
                      <tbody>
                        {preview.preview.slice(0, 20).map((r, i) => (
                          <tr key={i} className="border-t border-border">
                            {Object.keys(preview.preview[0]).map(k => <td key={k} className="p-2 text-muted-foreground">{String(r[k] ?? "-")}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.preview.length > 20 && <div className="p-2 text-[10px] text-muted-foreground italic border-t border-border">…and {preview.preview.length - 20} more rows</div>}
                  </div>
                )}
                {preview.errors.length > 0 && (
                  <div className="p-3 border-t border-border max-h-40 overflow-y-auto space-y-1">
                    {preview.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="text-[11px] text-destructive font-mono">Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
